import { useEffect, useRef, useState } from 'react'
import { countWords, mmss } from '@/components/teleprompter/prompter'

/**
 * Editing the script before you record (Brian's record.html v2.1 and v3.1):
 * a glass panel over the top of the screen, the cursor on the paragraph you
 * were reading and the keyboard up; the word count and read time update as you
 * type. Done keeps it, Cancel drops it, Restore brings back the written script.
 * Text A− / A+ sets the script's size, 24 to 40px.
 */
export function ScriptEditor({ open, paras, at, wpm, edited, written, tsize, onTsize, onDone, onCancel }: {
  open: boolean
  paras: string[]
  /** The paragraph at the reading line. */
  at: number
  wpm: number
  edited: boolean
  written: string
  tsize: number
  onTsize: (n: number) => void
  onDone: (text: string) => boolean
  onCancel: () => void
}) {
  const ta = useRef<HTMLTextAreaElement>(null)
  const [text, setText] = useState('')
  const [canRestore, setCanRestore] = useState(edited)

  useEffect(() => {
    if (!open) return
    const v = paras.join('\n\n')
    setText(v)
    setCanRestore(edited)
    const pos = paras.slice(0, at).reduce((n, p) => n + p.length + 2, 0)
    const t = setTimeout(() => {
      const el = ta.current
      if (!el) return
      el.focus({ preventScroll: true })
      try { el.setSelectionRange(pos, pos) } catch { /* */ }
      el.scrollTop = Math.max(0, (pos / Math.max(1, v.length)) * el.scrollHeight - 24)
    }, 60)
    return () => clearTimeout(t)
    // Opening is the trigger; the rest is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const n = countWords(text)
  return (
    <div className={`rec-ed${open ? ' on' : ''}`} role="dialog" aria-label="Edit the script" aria-hidden={!open}
      onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <div className="rec-ed__bar">
        <button type="button" onClick={() => { ta.current?.blur(); onCancel() }}>Cancel</button>
        <b>Edit script</b>
        <button type="button" className="done" onClick={() => { if (onDone(text)) ta.current?.blur() }}>Done</button>
      </div>
      <textarea ref={ta} spellCheck aria-label="Script" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="rec-ed__foot">
        <span>{n} words · {mmss((n / wpm) * 60)} at {wpm} wpm</span>
        <span className="tsz">
          <span>Text</span>
          <button type="button" aria-label="Smaller text" onClick={() => onTsize(tsize - 2)}>A−</button>
          <button type="button" aria-label="Larger text" onClick={() => onTsize(tsize + 2)}>A+</button>
        </span>
      </div>
      {canRestore && (
        <div className="rec-ed__foot" style={{ paddingTop: 0 }}>
          <button type="button" className="restore" onClick={() => { setText(written.trim()); setCanRestore(false) }}>Restore the written script</button>
        </div>
      )}
    </div>
  )
}
